import React from 'react';
import { LYT_INFO } from '../config/constants';

export default function PrivacyPage({ darkMode }) {
  const bg = darkMode ? '#0d1b2a' : '#ffffff';
  const text = darkMode ? '#e2e8f0' : '#334155';
  const heading = darkMode ? '#ffffff' : '#0f172a';
  const accent = darkMode ? '#c850c0' : '#0077B6';
  const cardBg = darkMode ? '#112240' : '#f8fafc';
  const border = darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)';

  const Section = ({ title, children }) => (
    <div style={{ marginBottom: 32 }}>
      <h2 style={{ color: heading, fontSize: 20, fontWeight: 600, marginBottom: 12 }}>{title}</h2>
      <div style={{ color: text, fontSize: 15, lineHeight: 1.7 }}>{children}</div>
    </div>
  );

  return (
    <div style={{ backgroundColor: bg, minHeight: '80vh', padding: '48px 20px' }}>
      <div style={{ maxWidth: 800, margin: '0 auto' }}>
        <h1 style={{ color: heading, fontSize: 32, fontWeight: 700, marginBottom: 8 }}>Privacy Policy</h1>
        <p style={{ color: accent, fontSize: 14, marginBottom: 40 }}>Last updated: March 4, 2026</p>

        <div style={{ background: cardBg, border: `1px solid ${border}`, borderRadius: 12, padding: 32 }}>
          <Section title="1. Who We Are">
            <p><strong>{LYT_INFO.name}</strong> ("we", "us", "our") is a fiber optic construction company based at {LYT_INFO.address}, {LYT_INFO.city}, {LYT_INFO.state} {LYT_INFO.zip}. This privacy policy explains how we collect, use, and protect your personal information when you use our website and internal workforce management platform at lytcomm.com.</p>
          </Section>

          <Section title="2. Information We Collect">
            <p><strong>Account Information:</strong> When you register as an employee or contractor, we collect your name, email address, phone number, mailing address, and role within the organization.</p>
            <p style={{ marginTop: 8 }}><strong>Employment & Contractor Data:</strong> For workforce management purposes, we may collect tax forms (W-4, W-9), insurance certificates, safety certifications, EIN numbers, and licensing information as required for employment or contractor engagement.</p>
            <p style={{ marginTop: 8 }}><strong>Operational Data:</strong> Our platform collects GPS location data (only during active work sessions with your consent), project progress data, production reports, photo documentation of work performed, and time tracking records.</p>
            <p style={{ marginTop: 8 }}><strong>Communication Data:</strong> If you opt in to SMS notifications, we collect your phone number for the purpose of sending business-related alerts such as payment status updates, compliance reminders, and project notifications.</p>
            <p style={{ marginTop: 8 }}><strong>Technical Data:</strong> We automatically collect browser type, IP address, and device information for security and platform functionality purposes.</p>
          </Section>

          <Section title="3. How We Use Your Information">
            <p>We use collected information to:</p>
            <ul style={{ margin: '8px 0', paddingLeft: 24 }}>
              <li>Manage employee and contractor onboarding, assignments, and payroll</li>
              <li>Track project progress, production reports, and quality control</li>
              <li>Process billing, invoices, and payments</li>
              <li>Send operational notifications (SMS and email) that you have opted into</li>
              <li>Maintain compliance with safety certifications and licensing requirements</li>
              <li>Generate reports for clients and internal management</li>
              <li>Ensure platform security and prevent unauthorized access</li>
            </ul>
          </Section>

          <Section title="4. SMS Messaging">
            <p>If you opt in to receive SMS notifications from {LYT_INFO.name}:</p>
            <ul style={{ margin: '8px 0', paddingLeft: 24 }}>
              <li>Messages are limited to business operations: payment confirmations, compliance alerts, and project notifications</li>
              <li>Message frequency varies based on business activity (typically 1-10 messages per month)</li>
              <li>Message and data rates may apply depending on your carrier</li>
              <li>You can opt out at any time by replying <strong>STOP</strong> to any message</li>
              <li>Reply <strong>HELP</strong> for assistance</li>
              <li>Your phone number will not be shared with third parties for marketing purposes</li>
            </ul>
          </Section>

          <Section title="5. Data Sharing">
            <p>We do not sell your personal information to third parties. We may share data with:</p>
            <ul style={{ margin: '8px 0', paddingLeft: 24 }}>
              <li><strong>Clients:</strong> Project progress reports and production data (no personal financial information)</li>
              <li><strong>Service Providers:</strong> We use Google Workspace for data storage, Vercel for hosting, Twilio for SMS delivery, and Anthropic for AI-assisted features. These providers process data on our behalf under their respective privacy policies.</li>
              <li><strong>Legal Requirements:</strong> We may disclose information if required by law, court order, or government request</li>
            </ul>
          </Section>

          <Section title="6. Data Security">
            <p>We implement reasonable security measures to protect your information, including encrypted connections (HTTPS), role-based access controls, and secure authentication. However, no method of electronic transmission or storage is 100% secure.</p>
          </Section>

          <Section title="7. Data Retention">
            <p>We retain personal information for as long as your account is active or as needed to provide services. Employment and contractor records are retained as required by applicable federal and state laws. You may request deletion of your account by contacting us.</p>
          </Section>

          <Section title="8. Your Rights">
            <p>You may request to access, correct, or delete your personal information by contacting us at <a href={`mailto:${LYT_INFO.email}`} style={{ color: accent }}>{LYT_INFO.email}</a> or calling {LYT_INFO.phone}.</p>
          </Section>

          <Section title="9. Changes to This Policy">
            <p>We may update this privacy policy from time to time. Changes will be posted on this page with an updated "Last updated" date.</p>
          </Section>

          <Section title="10. Contact Us">
            <p>If you have questions about this privacy policy, contact us at:</p>
            <p style={{ marginTop: 8 }}>
              {LYT_INFO.name}<br />
              {LYT_INFO.address}<br />
              {LYT_INFO.city}, {LYT_INFO.state} {LYT_INFO.zip}<br />
              {LYT_INFO.phone}<br />
              <a href={`mailto:${LYT_INFO.email}`} style={{ color: accent }}>{LYT_INFO.email}</a>
            </p>
          </Section>
        </div>
      </div>
    </div>
  );
}
